import React, { useState } from 'react';
import { Input } from "../../../components/ui/input";  
import { Label } from "../../../components/ui/label";
import { Card, CardContent } from "../../../components/ui/card";
import { Checkbox } from "../../../components/ui/checkbox";
import { CheckedState } from "@radix-ui/react-checkbox";
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Date as DateInput } from '../../../components/ui/date';

type CheckboxHandler = (checked: CheckedState) => void;

interface WeldingMachines {
  CRCE: {
    IWM: boolean;
    'P-625': boolean;
    'P-600Z': boolean;
    'M-500': boolean;
  };
  NonCRCE: boolean;
} 

interface WeldingFeature {
  enabled: boolean;
  machines: WeldingMachines;
}

interface FabricationOptionFeatures {
  materialTraceability: boolean;
  pipeFitter: boolean;
  welding: WeldingFeature;
  ndt: boolean;
  coating: boolean;
}

interface FabricationOption {
  selected: boolean;
  features: FabricationOptionFeatures;
}

interface FabricationOptions {
  buckleArrestor: FabricationOption;
  pipeInPipe: FabricationOption;
  bulkhead: FabricationOption;
}

interface ProjectData {
  projectName: string;
  projectNumber: string;
  location: string;
  customer: string;
  startDate: string;
  endDate: string;
  projectTypes: {
    pipeline: boolean;
    fabrication: boolean;
    others: boolean;
  };
  pipelineFeatures: FabricationOptionFeatures;
  fabricationOptions: FabricationOptions;
  othersFeatures: FabricationOptionFeatures;
}

interface ProjectCreationFormProps {
  onBack?: () => void;
}

const emptyWeldingMachines: WeldingMachines = {
  CRCE: {
    IWM: false,
    'P-625': false,
    'P-600Z': false,
    'M-500': false,
  },
  NonCRCE: false,
};

const emptyWeldingFeature: WeldingFeature = {
  enabled: false,
  machines: { ...emptyWeldingMachines },
};

const emptyFabricationOptionFeatures: FabricationOptionFeatures = {
  materialTraceability: false,
  pipeFitter: false,
  welding: { ...emptyWeldingFeature },
  ndt: false,
  coating: false,
};

const emptyFabricationOption: FabricationOption = {
  selected: false,
  features: { ...emptyFabricationOptionFeatures },
};

const ProjectCreationForm: React.FC<ProjectCreationFormProps> = ({ onBack }) => {
  const [formData, setFormData] = useState<ProjectData>({
    projectName: '',
    projectNumber: '',
    location: '',
    customer: '',
    startDate: '',
    endDate: '',
    projectTypes: {
      pipeline: false,
      fabrication: false,
      others: false,
    },
    pipelineFeatures: { ...emptyFabricationOptionFeatures },
    fabricationOptions: {
      buckleArrestor: { ...emptyFabricationOption },
      pipeInPipe: { ...emptyFabricationOption },
      bulkhead: { ...emptyFabricationOption },
    },
    othersFeatures: { ...emptyFabricationOptionFeatures },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (section: 'projectTypes', key: string): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => {
        const newData = {
          ...prev,
          [section]: {
            ...prev[section],
            [key]: checked,
          },
        };

        if (section === 'projectTypes') {
          if (!newData.projectTypes.fabrication) {
            newData.fabricationOptions = {
              buckleArrestor: { ...emptyFabricationOption },
              pipeInPipe: { ...emptyFabricationOption },
              bulkhead: { ...emptyFabricationOption },
            };
          }
          if (!newData.projectTypes.pipeline) {
            newData.pipelineFeatures = { ...emptyFabricationOptionFeatures };
          }
          if (!newData.projectTypes.others) {
            newData.othersFeatures = { ...emptyFabricationOptionFeatures };
          }
        }

        return newData;
      });
    };
  };

  const handleFabricationOptionChange = (option: keyof FabricationOptions): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        fabricationOptions: {
          ...prev.fabricationOptions,
          [option]: {
            ...prev.fabricationOptions[option],
            selected: checked === true,
            features: checked === true
              ? prev.fabricationOptions[option].features
              : { ...emptyFabricationOptionFeatures },
          },
        },
      }));
    };
  };

  const handleWeldingFeatureChange = (fabricationType: keyof FabricationOptions): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        fabricationOptions: {
          ...prev.fabricationOptions,
          [fabricationType]: {
            ...prev.fabricationOptions[fabricationType],
            features: {
              ...prev.fabricationOptions[fabricationType].features,
              welding: {
                ...prev.fabricationOptions[fabricationType].features.welding,
                enabled: checked === true,
                machines: checked === true
                  ? prev.fabricationOptions[fabricationType].features.welding.machines
                  : { ...emptyWeldingMachines },
              },
            },
          },
        },
      }));
    };
  };

  const handleWeldingMachineChange = (
    fabricationType: keyof FabricationOptions,
    machineType: 'CRCE' | 'NonCRCE',
    machine?: keyof WeldingMachines['CRCE']
  ): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        fabricationOptions: {
          ...prev.fabricationOptions,
          [fabricationType]: {
            ...prev.fabricationOptions[fabricationType],
            features: {
              ...prev.fabricationOptions[fabricationType].features,
              welding: {
                ...prev.fabricationOptions[fabricationType].features.welding,
                machines: {
                  ...prev.fabricationOptions[fabricationType].features.welding.machines,
                  ...(machineType === 'CRCE' && machine
                    ? {
                        CRCE: {
                          ...prev.fabricationOptions[fabricationType].features.welding.machines.CRCE,
                          [machine]: checked === true,
                        },
                      }
                    : {
                        [machineType]: checked === true,
                      }),
                },
              },
            },
          },
        },
      }));
    };
  };

  const handleFeatureChange = (
    fabricationType: keyof FabricationOptions,
    feature: keyof Omit<FabricationOptionFeatures, 'welding'>
  ): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        fabricationOptions: {
          ...prev.fabricationOptions,
          [fabricationType]: {
            ...prev.fabricationOptions[fabricationType],
            features: {
              ...prev.fabricationOptions[fabricationType].features,
              [feature]: checked === true,
            },
          },
        },
      }));
    };
  };

  const handlePipelineFeatureChange = (feature: keyof Omit<FabricationOptionFeatures, 'welding'>): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        pipelineFeatures: {
          ...prev.pipelineFeatures,
          [feature]: checked === true,
        },
      }));
    };
  };

  const handlePipelineWeldingFeatureChange: CheckboxHandler = (checked: CheckedState) => {
    setFormData(prev => ({
      ...prev,
      pipelineFeatures: {
        ...prev.pipelineFeatures,
        welding: {
          ...prev.pipelineFeatures.welding,
          enabled: checked === true,
          machines: checked === true
            ? prev.pipelineFeatures.welding.machines
            : { ...emptyWeldingMachines },
        },
      },
    }));
  };

  const handlePipelineWeldingMachineChange = (
    machineType: 'CRCE' | 'NonCRCE',
    machine?: keyof WeldingMachines['CRCE']
  ): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        pipelineFeatures: {
          ...prev.pipelineFeatures,
          welding: {
            ...prev.pipelineFeatures.welding,
            machines: {
              ...prev.pipelineFeatures.welding.machines,
              ...(machineType === 'CRCE' && machine
                ? {
                    CRCE: {
                      ...prev.pipelineFeatures.welding.machines.CRCE,
                      [machine]: checked === true,
                    },
                  }
                : {
                    [machineType]: checked === true,
                  }),
            },
          },
        },
      }));
    };
  };

  const handleOthersFeatureChange = (feature: keyof Omit<FabricationOptionFeatures, 'welding'>): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        othersFeatures: {
          ...prev.othersFeatures,
          [feature]: checked === true,
        },
      }));
    };
  };

  const handleOthersWeldingFeatureChange: CheckboxHandler = (checked: CheckedState) => {
    setFormData(prev => ({
      ...prev,
      othersFeatures: {
        ...prev.othersFeatures,
        welding: {
          ...prev.othersFeatures.welding,
          enabled: checked === true,
          machines: checked === true
            ? prev.othersFeatures.welding.machines
            : { ...emptyWeldingMachines },
        },
      },
    }));
  };

  const handleOthersWeldingMachineChange = (
    machineType: 'CRCE' | 'NonCRCE',
    machine?: keyof WeldingMachines['CRCE']
  ): CheckboxHandler => {
    return (checked: CheckedState) => {
      setFormData(prev => ({
        ...prev,
        othersFeatures: {
          ...prev.othersFeatures,
          welding: {
            ...prev.othersFeatures.welding,
            machines: {
              ...prev.othersFeatures.welding.machines,
              ...(machineType === 'CRCE' && machine
                ? {
                    CRCE: {
                      ...prev.othersFeatures.welding.machines.CRCE,
                      [machine]: checked === true,
                    },
                  }
                : {
                    [machineType]: checked === true,
                  }),
            },
          },
        },
      }));
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log(formData);
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            variant="outline"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
            </Button>
          <h1 className="text-2xl font-bold">Create New Project</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Name
              </label>
              <Input
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                placeholder="Enter project name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Number
              </label>
              <Input
                name="projectNumber"
                value={formData.projectNumber}
                onChange={handleChange}
                placeholder="Enter project number"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <Input
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Enter location"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer
              </label>
              <Input
                name="customer"
                value={formData.customer}
                onChange={handleChange}
                placeholder="Enter customer name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <DateInput
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <DateInput
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-[#865e9c] mb-3">Type of Project</h3>
            <div className="flex gap-6">
              <label className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.projectTypes.pipeline}
                  onCheckedChange={handleCheckboxChange("projectTypes", "pipeline")}
                  id="pipeline"
                />
                <span className="text-sm font-medium text-gray-700">Pipeline</span>
              </label>
              <label className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.projectTypes.fabrication}
                  onCheckedChange={handleCheckboxChange("projectTypes", "fabrication")}
                  id="fabrication"
                />
                <span className="text-sm font-medium text-gray-700">Fabrication</span>
              </label>
              <label className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.projectTypes.others}
                  onCheckedChange={handleCheckboxChange("projectTypes", "others")}
                  id="others"
                />
                <span className="text-sm font-medium text-gray-700">Others</span>
              </label>
            </div>

            {/* Pipeline Features */}
            {formData.projectTypes.pipeline && (
              <div className="mt-4 pl-8 border-l-2 border-[#865e9c] animate-in fade-in duration-500">
                <h3 className="text-lg font-semibold text-[#427290] mb-3">Pipeline Features</h3>
                <div className="space-y-4">
                  {/* Material Traceability */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.pipelineFeatures.materialTraceability}
                      onCheckedChange={handlePipelineFeatureChange('materialTraceability')}
                      id="pipeline-materialTraceability"
                    />
                    <Label htmlFor="pipeline-materialTraceability" className="text-sm">
                      Material Traceability
                    </Label>
                  </div>

                  {/* PipeFitter */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.pipelineFeatures.pipeFitter}
                      onCheckedChange={handlePipelineFeatureChange('pipeFitter')}
                      id="pipeline-pipeFitter"
                    />
                    <Label htmlFor="pipeline-pipeFitter" className="text-sm">
                      PipeFitter
                    </Label>
                  </div>

                  {/* Welding feature with machines */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        checked={formData.pipelineFeatures.welding.enabled}
                        onCheckedChange={handlePipelineWeldingFeatureChange}
                        id="pipeline-welding"
                      />
                      <Label htmlFor="pipeline-welding" className="text-sm">
                        Welding
                      </Label>
                    </div>

                    {formData.pipelineFeatures.welding.enabled && (
                      <div className="mt-2 pl-4 border-l border-[#60b885] animate-in fade-in duration-500">
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Checkbox
                                checked={Object.values(formData.pipelineFeatures.welding.machines.CRCE).some(v => v)}
                                onCheckedChange={(checked) => {
                                  const machines = Object.keys(formData.pipelineFeatures.welding.machines.CRCE) as (keyof WeldingMachines['CRCE'])[];
                                  machines.forEach(machine => {
                                    handlePipelineWeldingMachineChange('CRCE', machine)(checked);
                                  });
                                }}
                                id="pipeline-crce"
                              />
                              <Label htmlFor="pipeline-crce" className="text-xs font-medium">CRCE Machines</Label>
                            </div>
                            <div className="grid grid-cols-2 gap-2 pl-4">
                              {Object.keys(formData.pipelineFeatures.welding.machines.CRCE).map((machine) => {
                                const typedMachine = machine as keyof WeldingMachines['CRCE'];
                                return (
                                  <div key={machine} className="flex items-center gap-2">
                                    <Checkbox
                                      checked={formData.pipelineFeatures.welding.machines.CRCE[typedMachine]}
                                      onCheckedChange={handlePipelineWeldingMachineChange('CRCE', typedMachine)}
                                      id={`pipeline-machine-${machine}`}
                                    />
                                    <Label htmlFor={`pipeline-machine-${machine}`} className="text-xs">{machine}</Label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.pipelineFeatures.welding.machines.NonCRCE}
                              onCheckedChange={handlePipelineWeldingMachineChange('NonCRCE')}
                              id="pipeline-non-crce"
                            />
                            <Label htmlFor="pipeline-non-crce" className="text-xs font-medium">Non-CRCE Machines</Label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* NDT */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.pipelineFeatures.ndt}
                      onCheckedChange={handlePipelineFeatureChange('ndt')}
                      id="pipeline-ndt"
                    />
                    <Label htmlFor="pipeline-ndt" className="text-sm">
                      NDT
                    </Label>
                  </div>

                  {/* Coating */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.pipelineFeatures.coating}
                      onCheckedChange={handlePipelineFeatureChange('coating')}
                      id="pipeline-coating"
                    />
                    <Label htmlFor="pipeline-coating" className="text-sm">
                      Coating
                    </Label>
                  </div>
                </div>
              </div>
            )}

            {/* Fabrication Options */}
            {formData.projectTypes.fabrication && (
              <div className="mt-4 pl-8 border-l-2 border-[#865e9c] animate-in fade-in duration-500">
                <h3 className="text-lg font-semibold text-[#427290] mb-3">Fabrication Options</h3>
                <div className="flex gap-8">
                  {Object.entries({
                    buckleArrestor: 'Buckle Arrestor',
                    pipeInPipe: 'Pipe in Pipe',
                    bulkhead: 'Bulkhead'
                  }).map(([key, label]) => {
                    const optionKey = key as keyof FabricationOptions;
                    return (
                      <div key={key} className="flex-1">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={formData.fabricationOptions[optionKey].selected}
                            onCheckedChange={handleFabricationOptionChange(optionKey)}
                            id={`fab-${key}`}
                          />
                          <Label htmlFor={`fab-${key}`} className="text-sm font-medium">
                            {label}
                          </Label>
                        </div>

                        {formData.fabricationOptions[optionKey].selected && (
                          <div className="mt-2 pl-4 border-l border-[#427290] animate-in fade-in duration-500">
                            <div className="space-y-4">
                              {/* Material Traceability */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.fabricationOptions[optionKey].features.materialTraceability}
                                  onCheckedChange={handleFeatureChange(optionKey, 'materialTraceability')}
                                  id={`fab-${optionKey}-materialTraceability`}
                                />
                                <Label htmlFor={`fab-${optionKey}-materialTraceability`} className="text-sm">
                                  Material Traceability
                                </Label>
                              </div>

                              {/* PipeFitter */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.fabricationOptions[optionKey].features.pipeFitter}
                                  onCheckedChange={handleFeatureChange(optionKey, 'pipeFitter')}
                                  id={`fab-${optionKey}-pipeFitter`}
                                />
                                <Label htmlFor={`fab-${optionKey}-pipeFitter`} className="text-sm">
                                  PipeFitter
                                </Label>
                              </div>

                              {/* Welding feature with machines */}
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Checkbox
                                    checked={formData.fabricationOptions[optionKey].features.welding.enabled}
                                    onCheckedChange={handleWeldingFeatureChange(optionKey)}
                                    id={`fab-${optionKey}-welding`}
                                  />
                                  <Label htmlFor={`fab-${optionKey}-welding`} className="text-sm">
                                    Welding
                                  </Label>
                                </div>

                                {formData.fabricationOptions[optionKey].features.welding.enabled && (
                                  <div className="mt-2 pl-4 border-l border-[#60b885] animate-in fade-in duration-500">
                                    <div className="space-y-4">
                                      <div>
                                        <div className="flex items-center gap-2 mb-2">
                                          <Checkbox
                                            checked={Object.values(formData.fabricationOptions[optionKey].features.welding.machines.CRCE).some(v => v)}
                                            onCheckedChange={(checked) => {
                                              const machines = Object.keys(formData.fabricationOptions[optionKey].features.welding.machines.CRCE) as (keyof WeldingMachines['CRCE'])[];
                                              machines.forEach(machine => {
                                                handleWeldingMachineChange(optionKey, 'CRCE', machine)(checked);
                                              });
                                            }}
                                            id={`fab-${optionKey}-crce`}
                                          />
                                          <Label htmlFor={`fab-${optionKey}-crce`} className="text-xs font-medium">CRCE Machines</Label>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 pl-4">
                                          {Object.keys(formData.fabricationOptions[optionKey].features.welding.machines.CRCE).map((machine) => {
                                            const typedMachine = machine as keyof WeldingMachines['CRCE'];
                                            return (
                                              <div key={machine} className="flex items-center gap-2">
                                                <Checkbox
                                                  checked={formData.fabricationOptions[optionKey].features.welding.machines.CRCE[typedMachine]}
                                                  onCheckedChange={handleWeldingMachineChange(optionKey, 'CRCE', typedMachine)}
                                                  id={`fab-${optionKey}-machine-${machine}`}
                                                />
                                                <Label htmlFor={`fab-${optionKey}-machine-${machine}`} className="text-xs">{machine}</Label>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Checkbox
                                          checked={formData.fabricationOptions[optionKey].features.welding.machines.NonCRCE}
                                          onCheckedChange={handleWeldingMachineChange(optionKey, 'NonCRCE')}
                                          id={`fab-${optionKey}-non-crce`}
                                        />
                                        <Label htmlFor={`fab-${optionKey}-non-crce`} className="text-xs font-medium">Non-CRCE Machines</Label>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* NDT */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.fabricationOptions[optionKey].features.ndt}
                                  onCheckedChange={handleFeatureChange(optionKey, 'ndt')}
                                  id={`fab-${optionKey}-ndt`}
                                />
                                <Label htmlFor={`fab-${optionKey}-ndt`} className="text-sm">
                                  NDT
                                </Label>
                              </div>

                              {/* Coating */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={formData.fabricationOptions[optionKey].features.coating}
                                  onCheckedChange={handleFeatureChange(optionKey, 'coating')}
                                  id={`fab-${optionKey}-coating`}
                                />
                                <Label htmlFor={`fab-${optionKey}-coating`} className="text-sm">
                                  Coating
                                </Label>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Others Features */}
            {formData.projectTypes.others && (
              <div className="mt-4 pl-8 border-l-2 border-[#865e9c] animate-in fade-in duration-500">
                <h3 className="text-lg font-semibold text-[#427290] mb-3">Other Features</h3>
                <div className="space-y-4">
                  {/* Material Traceability */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.othersFeatures.materialTraceability}
                      onCheckedChange={handleOthersFeatureChange('materialTraceability')}
                      id="others-materialTraceability"
                    />
                    <Label htmlFor="others-materialTraceability" className="text-sm">
                      Material Traceability
                    </Label>
                  </div>

                  {/* PipeFitter */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.othersFeatures.pipeFitter}
                      onCheckedChange={handleOthersFeatureChange('pipeFitter')}
                      id="others-pipeFitter"
                    />
                    <Label htmlFor="others-pipeFitter" className="text-sm">
                      PipeFitter
                    </Label>
                  </div>

                  {/* Welding feature with machines */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        checked={formData.othersFeatures.welding.enabled}
                        onCheckedChange={handleOthersWeldingFeatureChange}
                        id="others-welding"
                      />
                      <Label htmlFor="others-welding" className="text-sm">
                        Welding
                      </Label>
                    </div>

                    {formData.othersFeatures.welding.enabled && (
                      <div className="mt-2 pl-4 border-l border-[#60b885] animate-in fade-in duration-500">
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Checkbox
                                checked={Object.values(formData.othersFeatures.welding.machines.CRCE).some(v => v)}
                                onCheckedChange={(checked) => {
                                  const machines = Object.keys(formData.othersFeatures.welding.machines.CRCE) as (keyof WeldingMachines['CRCE'])[];
                                  machines.forEach(machine => {
                                    handleOthersWeldingMachineChange('CRCE', machine)(checked);
                                  });
                                }}
                                id="others-crce"
                              />
                              <Label htmlFor="others-crce" className="text-xs font-medium">CRCE Machines</Label>
                            </div>
                            <div className="grid grid-cols-2 gap-2 pl-4">
                              {Object.keys(formData.othersFeatures.welding.machines.CRCE).map((machine) => {
                                const typedMachine = machine as keyof WeldingMachines['CRCE'];
                                return (
                                  <div key={machine} className="flex items-center gap-2">
                                    <Checkbox
                                      checked={formData.othersFeatures.welding.machines.CRCE[typedMachine]}
                                      onCheckedChange={handleOthersWeldingMachineChange('CRCE', typedMachine)}
                                      id={`others-machine-${machine}`}
                                    />
                                    <Label htmlFor={`others-machine-${machine}`} className="text-xs">{machine}</Label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.othersFeatures.welding.machines.NonCRCE}
                              onCheckedChange={handleOthersWeldingMachineChange('NonCRCE')}
                              id="others-non-crce"
                            />
                            <Label htmlFor="others-non-crce" className="text-xs font-medium">Non-CRCE Machines</Label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* NDT */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.othersFeatures.ndt}
                      onCheckedChange={handleOthersFeatureChange('ndt')}
                      id="others-ndt"
                    />
                    <Label htmlFor="others-ndt" className="text-sm">
                      NDT
                    </Label>
                  </div>

                  {/* Coating */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.othersFeatures.coating}
                      onCheckedChange={handleOthersFeatureChange('coating')}
                      id="others-coating"
                    />
                    <Label htmlFor="others-coating" className="text-sm">
                      Coating
                    </Label>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              className="w-40 bg-[#e04530] text-white py-2 rounded-lg hover:bg-[#e04530]/90 transition-colors"
            >
              Submit Project
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectCreationForm; 